import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  RiPlayLine,
  RiTestTubeLine,
  RiLoader2Line,
  RiCheckLine,
  RiAlertLine,
  RiArrowRightLine,
} from '@remixicon/react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useSandboxStore } from '@/stores/sandbox-store';

interface SandboxExecutionFlowProps {
  databaseId: string;
  query: string;
  onExecute: () => void;
  disabled?: boolean;
}

interface SandboxResult {
  success: boolean;
  impact: {
    queryType: string;
    isDestructive: boolean;
    affectedTables: string[];
    estimatedRowsAffected?: number;
    warnings: string[];
    canRollback: boolean;
    rollbackQuery?: string;
  };
  preview?: {
    rowsAffected: number;
    message: string;
  };
  error?: string;
}

export function SandboxExecutionFlow({
  databaseId,
  query,
  onExecute,
  disabled = false,
}: SandboxExecutionFlowProps) {
  const [testing, setTesting] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [sandboxResult, setSandboxResult] = useState<SandboxResult | null>(null);
  const [queryType, setQueryType] = useState<string>('UNKNOWN');

  const { isSandboxEnabledForQuery } = useSandboxStore();

  // Detect query type
  const detectQueryType = (sql: string): string => {
    const normalized = sql.trim().toLowerCase();
    if (normalized.startsWith('select')) return 'SELECT';
    if (normalized.startsWith('insert')) return 'INSERT';
    if (normalized.startsWith('update')) return 'UPDATE';
    if (normalized.startsWith('delete')) return 'DELETE';
    if (normalized.startsWith('alter')) return 'ALTER';
    if (normalized.startsWith('drop')) return 'DROP';
    if (normalized.startsWith('create')) return 'CREATE';
    if (normalized.startsWith('truncate')) return 'TRUNCATE';
    return 'UNKNOWN';
  };

  const currentQueryType = detectQueryType(query);
  const sandboxEnabled = isSandboxEnabledForQuery(currentQueryType, databaseId);

  const runSandboxTest = async () => {
    setTesting(true);
    setSandboxResult(null);

    try {
      const result = await api.testQueryInSandbox(databaseId, query);
      setSandboxResult(result);
      setQueryType(result.impact.queryType);

      if (result.success) {
        toast.success('Sandbox test completed successfully');
      } else {
        toast.error('Sandbox test failed');
      }
    } catch (error: any) {
      toast.error('Failed to run sandbox test');
      setSandboxResult({
        success: false,
        impact: {
          queryType: currentQueryType,
          isDestructive: false,
          affectedTables: [],
          warnings: [],
          canRollback: false,
        },
        error: error.message,
      });
    } finally {
      setTesting(false);
    }
  };

  const handleExecute = async () => {
    // If sandbox is enabled and we haven't tested yet, run sandbox first
    if (sandboxEnabled && !sandboxResult) {
      await runSandboxTest();
      return;
    }

    // Execute the actual query
    setExecuting(true);
    try {
      await onExecute();
      setSandboxResult(null); // Reset after execution
    } finally {
      setExecuting(false);
    }
  };

  const getButtonConfig = () => {
    if (sandboxEnabled && !sandboxResult) {
      // First click: Run in sandbox
      return {
        icon: RiTestTubeLine,
        text: testing ? 'Testing...' : 'Test in Sandbox',
        variant: 'default' as const,
        loading: testing,
      };
    } else if (sandboxResult && sandboxResult.success) {
      // After successful sandbox: Execute for real
      return {
        icon: RiPlayLine,
        text: executing ? 'Executing...' : 'Execute for Real',
        variant: sandboxResult.impact.isDestructive ? ('destructive' as const) : ('default' as const),
        loading: executing,
      };
    } else {
      // Direct execution (sandbox disabled)
      return {
        icon: RiPlayLine,
        text: executing ? 'Executing...' : 'Execute Query',
        variant: 'default' as const,
        loading: executing,
      };
    }
  };

  const buttonConfig = getButtonConfig();
  const Icon = buttonConfig.loading ? RiLoader2Line : buttonConfig.icon;

  return (
    <div className="space-y-3">
      {/* Sandbox Result - Fixed Layout */}
      {sandboxResult && (
        <div className="space-y-2 mb-3">
          {sandboxResult.success ? (
            <Alert className="border-green-200 bg-green-50 dark:bg-green-950/30">
              <div className="flex items-start gap-3 w-full">
                <RiCheckLine className="h-5 w-5 text-green-600 dark:text-green-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-green-900 dark:text-green-100 text-sm">
                    Sandbox Test Passed
                  </p>
                  <p className="text-green-700 dark:text-green-300 text-xs mt-1">
                    {sandboxResult.preview?.message || 'Query executed successfully (changes rolled back)'}
                  </p>
                  {sandboxResult.preview?.rowsAffected !== undefined && (
                    <p className="text-green-600 dark:text-green-400 text-xs mt-1.5">
                      Would affect <span className="font-semibold">{sandboxResult.preview.rowsAffected}</span> {sandboxResult.preview.rowsAffected === 1 ? 'row' : 'rows'}
                    </p>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSandboxResult(null)}
                    className="text-xs h-7 mt-2 px-2"
                  >
                    Reset Test
                  </Button>
                </div>
              </div>
            </Alert>
          ) : (
            <Alert variant="destructive">
              <RiAlertLine className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <p className="font-medium">Sandbox Test Failed</p>
                <p className="text-xs mt-0.5">{sandboxResult.error || 'Query would fail'}</p>
              </AlertDescription>
            </Alert>
          )}

          {/* Warnings */}
          {sandboxResult.impact.warnings.length > 0 && (
            <div className="space-y-1">
              {sandboxResult.impact.warnings.slice(0, 2).map((warning, idx) => (
                <Alert key={idx} variant={warning.includes('🚨') ? 'destructive' : 'default'} className="py-2">
                  <AlertDescription className="text-xs">{warning}</AlertDescription>
                </Alert>
              ))}
            </div>
          )}

          {/* Rollback Info */}
          {sandboxResult.impact.canRollback && sandboxResult.impact.rollbackQuery && (
            <div className="text-xs text-muted-foreground bg-muted px-3 py-2 rounded-md">
              <p className="font-medium mb-1">Rollback available if needed</p>
              <code className="text-[10px]">{sandboxResult.impact.rollbackQuery.slice(0, 100)}...</code>
            </div>
          )}
        </div>
      )}

      {/* Execute Button */}
      <div className="flex items-center gap-2">
        <Button
          onClick={handleExecute}
          disabled={disabled || buttonConfig.loading}
          variant={buttonConfig.variant}
          className="gap-2"
        >
          <Icon className={buttonConfig.loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          {buttonConfig.text}
        </Button>

        {/* Status Indicators */}
        {sandboxEnabled && !sandboxResult && (
          <Badge variant="outline" className="text-xs gap-1">
            <RiTestTubeLine className="h-3 w-3" />
            Sandbox Mode
          </Badge>
        )}

        {sandboxResult && sandboxResult.success && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <RiCheckLine className="h-3 w-3 text-green-600 dark:text-green-500" />
            <span>Tested</span>
            <RiArrowRightLine className="h-3 w-3" />
            <span>Ready to execute</span>
          </div>
        )}
      </div>
    </div>
  );
}
